import { useEffect } from 'react';
import { Map, StyleSpecification } from 'maplibre-gl';
import { BasemapStyle } from '../../../store/types';
import { selectCurrentBackgroundColor, selectUseSolidBackground, selectCurrentBackgroundOpacity } from '../../../store/basemapSlice'; // Aggiunto import
import useGeoData from '../../../store/useGeoData';

// Definisci gli ID costanti per sorgenti e layer
const RASTER_BASEMAP_SOURCE_ID = 'raster-basemap-source';
const RASTER_BASEMAP_LAYER_ID = 'raster-basemap-layer';

interface UseBasemapSyncProps {
    map: Map | null;
}

export const useBasemapSync = ({ map }: UseBasemapSyncProps) => {
    const activeBasemapId = useGeoData(state => state.activeBasemapId);
    const availableBasemaps = useGeoData(state => state.availableBasemaps);
    const clearZoomToLayerExtent = useGeoData(state => state.clearZoomToLayerExtent);
    const currentBackgroundColor = useGeoData(selectCurrentBackgroundColor);
    const useSolidBackground = useGeoData(selectUseSolidBackground);
    const currentBackgroundOpacity = useGeoData(selectCurrentBackgroundOpacity);

    useEffect(() => {
        if (!map) {
            return;
        }

        clearZoomToLayerExtent();

        const currentView = {
            center: map.getCenter(),
            zoom: map.getZoom(),
            bearing: map.getBearing(),
            pitch: map.getPitch(),
        };

        const cleanupRasterBasemap = () => {
            if (!map || !map.getStyle()) {
                 return;
            }
            try {
                if (map.getLayer(RASTER_BASEMAP_LAYER_ID)) {
                    map.removeLayer(RASTER_BASEMAP_LAYER_ID);
                }
            } catch (e) {
                // console.warn(`Avviso durante la rimozione del layer raster ${RASTER_BASEMAP_LAYER_ID}:`, e);
            }
            try {
                if (map.getSource(RASTER_BASEMAP_SOURCE_ID)) {
                    map.removeSource(RASTER_BASEMAP_SOURCE_ID);
                }
            } catch (e) {
                // console.warn(`Avviso durante la rimozione della sorgente raster ${RASTER_BASEMAP_SOURCE_ID}:`, e);
            }
        };

        if (useSolidBackground) {
            // Mostra un layer di background colorato
            cleanupRasterBasemap(); // Rimuovi eventuale basemap raster precedente

            const backgroundOnlyStyle: StyleSpecification = {
                version: 8,
                name: 'Solid Background',
                sources: {},
                layers: [
                    {
                        id: 'background-layer',
                        type: 'background',
                        paint: {
                            'background-color': currentBackgroundColor || '#f0f0f0',
                            'background-opacity': typeof currentBackgroundOpacity === 'number' ? currentBackgroundOpacity : 1,
                        },
                    },
                ],
            };
            try {
                map.setStyle(backgroundOnlyStyle);
                map.once('style.load', () => { // 'style.load' è più affidabile di 'load' dopo setStyle
                    map.jumpTo(currentView);
                });
            } catch (error) {
                console.error(`[useBasemapSync] Errore nell'impostare lo stile background-only:`, error);
            }

        } else {
            // Mostra una basemap tradizionale (vector o raster)
            if (!activeBasemapId || !availableBasemaps) {
                // Se non c'è una basemap attiva, potremmo voler impostare uno stile vuoto o di fallback
                // Per ora, non facciamo nulla se non c'è una basemap attiva quando useSolidBackground è false
                // Potrebbe essere utile rimuovere il layer 'background-layer' se esistente
                if (map.getLayer('background-layer')) {
                    try {
                        map.removeLayer('background-layer');
                    } catch (e) {
                        // console.warn("Impossibile rimuovere background-layer", e);
                    }
                }
                return;
            }

            const basemap: BasemapStyle | undefined = availableBasemaps.find((b: BasemapStyle) => b.id === activeBasemapId);

            if (!basemap) {
                console.warn(`[useBasemapSync] Basemap attiva con ID "${activeBasemapId}" non trovata.`);
                return;
            }

            if (basemap.type === 'vector') {
                cleanupRasterBasemap();
                 // Rimuovi il layer di background solido se presente
                if (map.getLayer('background-layer')) {
                    try {
                        map.removeLayer('background-layer');
                    } catch (e) {
                        // console.warn("Impossibile rimuovere background-layer per basemap vettoriale", e);
                    }
                }

                if (!basemap.styleUrl) {
                    console.error(`Basemap vettoriale con ID "${basemap.id}" non ha uno styleUrl definito.`);
                    return;
                }
                try {
                    map.setStyle(basemap.styleUrl);
                    map.once('style.load', () => {
                        map.jumpTo(currentView);
                    });
                } catch (error) {
                    console.error(`[useBasemapSync] Errore nell'impostare lo stile vettoriale ${basemap.styleUrl}:`, error);
                }

            } else if (basemap.type === 'raster') {
                if (!basemap.tilesUrl) {
                    console.error(`[useBasemapSync] Basemap raster con ID "${basemap.id}" non ha un tilesUrl definito.`);
                    return;
                }

                // Costruisci una StyleSpecification completa per la basemap raster
                const currentMapStyle = map.getStyle();
                const glyphsUrl = currentMapStyle?.glyphs;
                const spriteUrl = currentMapStyle?.sprite;

                const rasterStyleSpec: StyleSpecification = {
                    version: 8,
                    name: basemap.name || 'Raster Basemap',
                    sources: {
                        [RASTER_BASEMAP_SOURCE_ID]: {
                            type: 'raster',
                            tiles: [basemap.tilesUrl],
                            tileSize: 256, // Valore standard
                            attribution: basemap.attribution || '',
                            minzoom: basemap.minZoom || 0, // Valori di default ragionevoli
                            maxzoom: basemap.maxZoom || 22, // Valori di default ragionevoli
                        }
                    },
                    layers: [
                        // Opzionale: un layer di background sotto il raster, se desiderato.
                        // {
                        //   id: 'background-fill-under-raster', // ID univoco
                        //   type: 'background',
                        //   paint: {
                        //     'background-color': '#f0f0f0', // Un colore di fallback o configurabile
                        //     'background-opacity': 1
                        //   }
                        // },
                        {
                            id: RASTER_BASEMAP_LAYER_ID,
                            type: 'raster',
                            source: RASTER_BASEMAP_SOURCE_ID,
                            paint: {
                                'raster-fade-duration': 100 // Leggera dissolvenza per transizione più fluida
                            }
                        }
                    ]
                };

                // Aggiungi glyphs e sprite solo se sono definiti nello stile precedente
                if (glyphsUrl) {
                    rasterStyleSpec.glyphs = glyphsUrl;
                }
                if (spriteUrl) {
                    rasterStyleSpec.sprite = spriteUrl;
                }

                try {
                    // Usa map.setStyle per applicare la nuova basemap raster.
                    // Questo rimuoverà completamente lo stile precedente (vettoriale, solido, o altra raster).
                    map.setStyle(rasterStyleSpec);
                    map.once('style.load', () => {
                        map.jumpTo(currentView); // Ripristina la vista dopo il caricamento dello stile
                    });
                } catch (error) {
                    console.error(`[useBasemapSync] Errore nell'impostare lo stile raster per ${basemap.id}:`, error);
                }
            }
        }
    }, [map, useSolidBackground, currentBackgroundColor, currentBackgroundOpacity, activeBasemapId, availableBasemaps, clearZoomToLayerExtent]);
};